export interface EventDrivenWaiterOptions {
  // Registers a listener for pushed events; the returned function unsubscribes.
  // The passed `wake` resolves the in-flight wait() (or latches for the next
  // wait() if none is pending).
  subscribe: (wake: () => void) => () => void;
  // Backstop re-check cadence when no event arrives (default 1000ms).
  reconcileIntervalMs?: number;
}

export interface EventDrivenWaiter {
  // Resolves on the first of: a pushed event, the reconcile interval, or stop().
  // If an event/wakeNow arrived since the last wait() resolved, returns
  // immediately (the wake is latched, not dropped).
  wait: () => Promise<void>;
  // Trigger an immediate wake of the in-flight wait() — e.g. from a step's
  // _stop/_pause so it observes the stop without waiting a full interval.
  wakeNow: () => void;
  // Idempotent teardown: unsubscribe and unblock any pending wait().
  stop: () => void;
  isStopped: () => boolean;
}

/**
 * Shared primitive for steps that react to a bot's pushed state transitions
 * instead of polling. A step's monitor loop awaits `wait()`, which resolves the
 * instant a subscribed event fires (fast path) or after `reconcileIntervalMs`
 * as a backstop if an event is ever dropped. The step re-reads authoritative
 * state itself after each `wait()`, so a missed event costs at most one
 * reconcile of latency, never a lost transition.
 *
 * Wakes that arrive while no `wait()` is pending (e.g. a second transition
 * during the state-read a first event triggered) are latched, so the next
 * `wait()` returns immediately rather than waiting a full reconcile interval.
 *
 * Single-subscriber assumption: `subscribe`'s returned unsubscribe typically
 * resolves to a connector `unsubscribeFrom*` called with no subscription token,
 * which removes ALL callbacks for the topic key (see MessengerNode.unsubscribe).
 * That is a property of how these call sites invoke it, not of the messenger:
 * `subscribe` returns a token and `unsubscribeFrom*` accepts one, and passing it
 * removes only that registration. It is fine here because the steps using this
 * are sequential foreground steps — only one waiter subscribes to a given topic
 * at a time — matching how other topic consumers (e.g. CppGripperProxy) manage
 * their subscription lifecycle. A consumer that shares a topic with a long-lived
 * subscriber must hold and pass its token instead.
 *
 * This intentionally lives outside the base Step class: each step keeps its own
 * loop (deadlines, pause handling, predicate/handlers) and shares only the
 * subscribe + wake + reconcile-timer + idempotent-teardown plumbing.
 */
export function createEventDrivenWaiter(
  options: EventDrivenWaiterOptions,
): EventDrivenWaiter {
  const { subscribe, reconcileIntervalMs = 1000 } = options;

  let stopped = false;
  // Set when a wake fires while no wait() is pending, so the next wait()
  // resolves immediately instead of dropping the wake.
  let pendingWake = false;
  let wake: (() => void) | null = null;

  const signal = (): void => {
    if (wake) {
      wake();
    } else {
      pendingWake = true;
    }
  };

  const unsubscribe = subscribe(() => {
    if (!stopped) {
      signal();
    }
  });

  return {
    wait: () =>
      new Promise<void>((resolve) => {
        if (stopped || pendingWake) {
          pendingWake = false;
          resolve();

          return;
        }

        const timer = setTimeout(() => {
          wake = null;
          resolve();
        }, reconcileIntervalMs);

        wake = () => {
          clearTimeout(timer);
          wake = null;
          resolve();
        };
      }),

    wakeNow: signal,

    stop: () => {
      if (stopped) {
        return;
      }

      stopped = true;
      wake?.();
      unsubscribe();
    },

    isStopped: () => stopped,
  };
}
